import { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, Trash2, Loader2, UtensilsCrossed, Image } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { getMenu, uploadMenuImage, deleteMenuImage } from '../../lib/api';
import { toast } from 'sonner';

export const AdminMenuPage = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await getMenu();
      setImages(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Solo immagini consentite');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File troppo grande (max 5MB)');
      return;
    }

    setUploading(true);
    try {
      await uploadMenuImage(file);
      toast.success('Immagine caricata');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (imageId) => {
    try {
      await deleteMenuImage(imageId);
      toast.success('Immagine eliminata');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="admin-menu">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">GESTIONE MENU</h1>
          <p className="text-slate-400">
            Carica le immagini del menu della cucina
          </p>
        </div>

        {/* Upload Section */}
        <Card className="bg-slate-900 border-slate-800 mb-8">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gold/10 rounded-lg">
                  <UtensilsCrossed className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <h3 className="font-heading font-bold">Carica Immagine Menu</h3>
                  <p className="text-sm text-slate-400">Formati: JPG, PNG, WebP (max 5MB)</p>
                </div>
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleUpload}
                className="hidden"
                id="menu-upload"
                data-testid="menu-upload-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-gold text-slate-950 hover:bg-gold/90"
                data-testid="upload-menu-btn"
              >
                {uploading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Upload className="w-5 h-5 mr-2" />
                )}
                {uploading ? 'Caricamento...' : 'Carica Immagine'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Images Grid */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Image className="w-5 h-5" />
              Immagini Caricate ({images.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {images.length > 0 ? (
              <div className="grid gap-6 md:grid-cols-2">
                {images.map(image => (
                  <div 
                    key={image.id} 
                    className="relative group overflow-hidden rounded-lg border border-slate-800"
                  >
                    <img
                      src={`data:${image.content_type};base64,${image.data}`}
                      alt={image.filename}
                      className="w-full h-auto"
                    />
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            variant="destructive"
                            className="bg-red-500 hover:bg-red-600"
                            data-testid={`delete-menu-${image.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Elimina
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-slate-900 border-slate-800">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Elimina immagine</AlertDialogTitle>
                            <AlertDialogDescription>
                              Sei sicuro di voler eliminare questa immagine del menu?
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-slate-800 border-slate-700">
                              Annulla
                            </AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleDelete(image.id)}
                              className="bg-red-500 hover:bg-red-600"
                            >
                              Elimina
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                    
                    {/* Filename */}
                    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                      <p className="text-sm text-white truncate">{image.filename}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <UtensilsCrossed className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">Nessuna immagine caricata</p>
                <p className="text-sm text-slate-500 mt-1">
                  Carica le immagini del menu della cucina
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminMenuPage;
